
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "./types";
import { toast } from "@/hooks/use-toast";

// Get current user's subscription
export const getCurrentSubscription = async (): Promise<Subscription | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  try {
    // Use standard supabase query instead of RPC
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error("Error fetching subscription:", error);
      // Create default free subscription
      return createDefaultSubscription(session.user.id);
    }

    // If no subscription found, create a default one
    if (!data || data.length === 0) {
      return createDefaultSubscription(session.user.id);
    }

    return formatSubscription(data[0]);
  } catch (error) {
    console.error("Error in getCurrentSubscription:", error);
    return null;
  }
};

// Create a default free subscription for the user
const createDefaultSubscription = async (userId: string): Promise<Subscription | null> => {
  try {
    // Use standard supabase query instead of RPC
    const { data, error } = await supabase
      .from('subscriptions')
      .insert([{
        user_id: userId,
        tier: 'free',
        status: 'active'
      }])
      .select();

    if (error) {
      console.error("Error creating default subscription:", error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    return formatSubscription(data[0]);
  } catch (error) {
    console.error("Error in createDefaultSubscription:", error);
    return null;
  }
};

// Check subscription status
export const checkSubscriptionStatus = async (): Promise<void> => {
  try {
    const { data, error } = await supabase.functions.invoke('check-subscription');
    if (error) throw error;
    
    // Update local subscription if needed
    if (data.subscription) {
      // You could implement logic to update the local subscription state here
    }
  } catch (error) {
    console.error('Error checking subscription status:', error);
  }
};

// Helper to format subscription data
export const formatSubscription = (data: any): Subscription => {
  return {
    id: data.id,
    tier: data.tier || 'free',
    stripeSubscriptionId: data.stripe_subscription_id,
    stripeCustomerId: data.stripe_customer_id,
    currentPeriodStart: data.current_period_start ? new Date(data.current_period_start) : undefined,
    currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : undefined,
    status: data.status || 'active',
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
};
